import React, { useState, useEffect } from 'react';
import { LoginAnnouncements } from '../announcements/Announcements';
import { useAuth } from '../../context/AuthContext';
import { ApiClient } from '../../api/client';
import { ThemeToggle } from '../common/ThemeToggle';
import {
  Lock,
  Mail,
  ArrowRight,
  Sparkles,
  AlertCircle,
  Smartphone,
  Key,
  CheckCircle,
  ExternalLink,
  X,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

import { useSystemBranding, ShieldLogo, BrandTitle } from '../../context/SystemBrandingContext';

// ── BIZTONSÁGI & DEMO KONFIGURÁCIÓ ──────────────────────────────────────────
// Éles (production) környezetben állítsa FALSE-ra (vagy a VITE_ENABLE_DEMO=false környezeti változóval)!
// Ha FALSE:
// 1. A gyors tesztfiókok panel teljesen rejtve marad.
// 2. Nem próbálkozik az automatikus teszt jelszóval.
// 3. Nem jelenik meg a demó jelszóra utaló kék információs doboz sem.
export const ENABLE_DEMO_MODE = true;

// Szervezeti egyedi belépési oldal: /login/<token>. A token véletlen, a szervezet
// nevéből/slugjából nem következtethető ki (lásd worker: tenants.login_token).
const ORG_LOGIN_PATH_RE = /^\/login\/([A-Za-z0-9_-]{24,64})\/?$/;
export function getOrgLoginToken(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(ORG_LOGIN_PATH_RE);
  return m ? m[1] : null;
}

type OrgLoginInfo = {
  name: string;
  short_name?: string | null;
  logo_url?: string | null;
  login_subtitle?: string | null;
  brand_color?: string | null;
};

export const LoginView: React.FC = () => {
  const { login, verify2FA } = useAuth();
  const { branding } = useSystemBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [demoUsers, setDemoUsers] = useState<Array<{ email: string; role: string; name?: string; tenant_id?: number; tenant_name?: string }>>([]);

  // Szervezeti egyedi belépési oldal (ha az URL /login/<token>)
  const [orgToken] = useState<string | null>(() => getOrgLoginToken());
  const [orgInfo, setOrgInfo] = useState<OrgLoginInfo | null>(null);
  const [orgLinkInvalid, setOrgLinkInvalid] = useState(false);

  // 2FA Challenge State
  const [twoFaChallenge, setTwoFaChallenge] = useState<{ tempToken: string } | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');

  // Forgot Password Modal State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState<{ type: 'success' | 'error'; text: string; link?: string } | null>(null);

  useEffect(() => {
    // Szervezeti oldal: a szervezet adatai és KIZÁRÓLAG a saját, engedélyezett felhasználói
    if (orgToken) {
      ApiClient.getOrgLoginPage(orgToken)
        .then((res) => {
          if (res.ok && res.tenant) {
            setOrgInfo(res.tenant);
            setDemoUsers(ENABLE_DEMO_MODE && Array.isArray(res.users) ? res.users : []);
          } else {
            setOrgLinkInvalid(true);
            setDemoUsers([]);
          }
        })
        .catch(() => setOrgLinkInvalid(true));
      return;
    }
    if (!ENABLE_DEMO_MODE) {
      setDemoUsers([]);
      return;
    }
    const fetchDemoUsers = async () => {
      try {
        // Központi oldal: csak a rendszerszervezet superadmin által engedélyezett felhasználói
        const res = await ApiClient.getDemoUsers();
        if (res.ok && Array.isArray(res.data)) {
          setDemoUsers(res.data);
        } else {
          setDemoUsers([]);
        }
      } catch {
        setDemoUsers([]);
      }
    };
    fetchDemoUsers();
  }, [orgToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Kérjük adja meg az e-mail címet és a jelszót!');
      return;
    }

    setLoading(true);
    setError(null);
    setInfoMsg(null);

    const res = await login(email, password, orgToken && !orgLinkInvalid ? orgToken : undefined);
    if (res.requires_2fa && res.temp_token) {
      setTwoFaChallenge({ tempToken: res.temp_token });
      setTwoFaCode('');
    } else if (!res.ok) {
      setError(res.error || 'Hibás e-mail cím vagy jelszó!');
    }
    setLoading(false);
  };

  const handleVerify2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFaChallenge) return;
    if (twoFaCode.length !== 6) {
      setError('Kérjük, adja meg a 6 jegyű hitelesítő kódot!');
      return;
    }

    setLoading(true);
    setError(null);
    setInfoMsg(null);

    const res = await verify2FA(twoFaChallenge.tempToken, twoFaCode);
    if (!res.ok) {
      setError(res.error || 'Érvénytelen vagy lejárt 2FA kód');
    }
    setLoading(false);
  };

  const handleQuickDemoLogin = async (demoEmail: string) => {
    setEmail(demoEmail);
    setError(null);
    setInfoMsg(null);
    setTwoFaChallenge(null);

    // Próbáljuk meg a demó alapértelmezett jelszavával ('Admin1234!') beléptetni
    setLoading(true);
    const res = await login(demoEmail, 'Admin1234!', orgToken && !orgLinkInvalid ? orgToken : undefined);
    setLoading(false);

    if (res.ok) {
      setPassword('Admin1234!');
      return;
    }

    if (res.requires_2fa && res.temp_token) {
      setPassword('Admin1234!');
      setTwoFaChallenge({ tempToken: res.temp_token });
      setTwoFaCode('');
      return;
    }

    // Ha a jelszó eltér az alapértelmezettől (pl. megváltoztatták a jelszót):
    // NEM jelenítünk meg piros hibát, hanem kiürítjük a jelszómezőt és megvárjuk, amíg begépelik az új jelszót!
    setPassword('');
    setInfoMsg('A fiók jelszava eltér az alapértelmezett demó jelszótól. Kérjük, gépelje be az egyedi jelszót a belépéshez!');

    setTimeout(() => {
      const pwdInput = document.getElementById('login-password-input') as HTMLInputElement;
      if (pwdInput) {
        pwdInput.focus();
        pwdInput.select();
      }
    }, 60);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;

    setForgotLoading(true);
    setForgotMsg(null);

    const res = await ApiClient.forgotPassword(forgotEmail);
    if (res.ok) {
      setForgotMsg({
        type: 'success',
        text: 'A jelszó-visszaállító hivatkozást sikeresen legeneráltuk és elküldtük.',
        link: res.reset_link
      });
    } else {
      setForgotMsg({
        type: 'error',
        text: res.error || 'Hiba történt a kérés feldolgozása során'
      });
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-100 dark:bg-[#070a12] text-slate-900 dark:text-slate-100 transition-colors duration-300 relative overflow-hidden">
      {/* Background Gradient Shapes */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-500/10 dark:bg-indigo-500/15 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/15 rounded-full blur-3xl pointer-events-none"></div>

      {/* Theme Toggle in Header Corner */}
      <div className="absolute top-6 right-6 z-20">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md z-10">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <ShieldLogo size="xl" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <BrandTitle size="xl" />
            {branding.version && (
              <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/80 shadow-2xs">
                {branding.version}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {branding.subtitle || '2011. évi CXII. törvény szerinti közzétételi platform'}
          </p>
          {orgInfo && (
            <div className="mt-5 inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-white/80 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 shadow-sm max-w-full">
              {orgInfo.logo_url && (
                <img src={orgInfo.logo_url} alt="" className="w-9 h-9 object-contain rounded-lg shrink-0" />
              )}
              <div className="text-left min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Szervezeti belépés</div>
                <div className="text-sm font-bold text-slate-900 dark:text-white truncate" style={orgInfo.brand_color ? { color: orgInfo.brand_color } : undefined}>
                  {orgInfo.name}
                </div>
                {orgInfo.login_subtitle && (
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{orgInfo.login_subtitle}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Rendszerüzenetek: központi oldalon a központi, szervezeti linken az adott szervezetnek szólók */}
        {!orgLinkInvalid && <LoginAnnouncements loginToken={orgToken} className="mb-4" />}

        {/* Login / 2FA Card */}
        <div className="bg-white dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl dark:shadow-2xl relative overflow-hidden transition-colors">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500"></div>

          {orgLinkInvalid && (
            <div className="mb-6 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl flex items-start gap-3 text-amber-800 dark:text-amber-300 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
              <div>
                <p className="font-semibold">Érvénytelen vagy megszűnt belépési hivatkozás</p>
                <p className="text-xs mt-0.5">Kérje a szervezete adminisztrátorától az aktuális belépési linket, vagy lépjen be a <a href="/" className="underline font-semibold">központi belépési oldalon</a>.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-3.5 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-xl flex items-center gap-3 text-rose-700 dark:text-rose-300 text-sm animate-in fade-in">
              <AlertCircle className="w-5 h-5 shrink-0 text-rose-500 dark:text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          {ENABLE_DEMO_MODE && infoMsg && !error && (
            <div className="mb-6 p-3.5 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl flex items-start gap-3 text-indigo-800 dark:text-cyan-300 text-sm animate-in fade-in">
              <Key className="w-5 h-5 shrink-0 text-indigo-600 dark:text-cyan-400 mt-0.5" />
              <div>
                <p className="font-semibold text-xs text-indigo-950 dark:text-cyan-200">Egyedi jelszó szükséges</p>
                <p className="text-xs text-indigo-700 dark:text-cyan-400/90 mt-0.5">
                  {infoMsg}
                </p>
              </div>
            </div>
          )}

          {/* NORMAL LOGIN OR 2FA CHALLENGE */}
          {twoFaChallenge ? (
            /* 2FA CHALLENGE FORM */
            <form onSubmit={handleVerify2FASubmit} className="space-y-4">
              <div className="text-center pb-2">
                <div className="w-12 h-12 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-2 border border-indigo-500/20">
                  <Smartphone className="w-6 h-6" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Kétlépcsős Azonosítás (2FA)</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Adja meg a telefonos Authenticator appban megjelenő 6 jegyű kódot a belépés befejezéséhez.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5 text-center">
                  6-jegyű Ellenőrző Kód
                </label>
                <input
                  type="text"
                  maxLength={6}
                  autoFocus
                  required
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full text-center tracking-[0.5em] text-2xl font-mono font-bold py-3 bg-slate-50 dark:bg-slate-950/70 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading || twoFaCode.length !== 6}
                className="w-full mt-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl text-sm shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span>Hitelesítés & Belépés</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setTwoFaChallenge(null);
                    setTwoFaCode('');
                    setError(null);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Vissza a jelszavas belépéshez
                </button>
              </div>
            </form>
          ) : (
            /* STANDARD LOGIN FORM */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                  E-mail Cím
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (infoMsg) setInfoMsg(null);
                      if (error) setError(null);
                    }}
                    placeholder="admin@onkormanyzat.hu"
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950/70 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    Jelszó
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(email);
                      setForgotMsg(null);
                      setShowForgotModal(true);
                    }}
                    className="text-[11px] text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 hover:underline transition cursor-pointer"
                  >
                    Elfelejtett jelszó?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (infoMsg) setInfoMsg(null);
                      if (error) setError(null);
                    }}
                    placeholder={infoMsg ? 'Kérjük, adja meg egyedi jelszavát...' : '••••••••'}
                    className={`w-full pl-10 pr-11 py-2.5 bg-slate-50 dark:bg-slate-950/70 border rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 transition-all ${infoMsg
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                        : 'border-slate-300 dark:border-slate-700 focus:ring-indigo-500'
                      }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                    title={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                    aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-medium rounded-xl text-sm shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span>Bejelentkezés</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Quick Demo Accounts Switcher */}
          {ENABLE_DEMO_MODE && !twoFaChallenge && demoUsers.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 animate-in fade-in">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>{orgInfo ? 'A szervezet felhasználói (1-kattintásos belépés)' : 'Gyors Tesztfiókok (1-kattintásos belépés)'}</span>
                </div>
                <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800">
                  {demoUsers.length} {orgInfo ? 'fiók' : 'tesztfiók'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {demoUsers.map((u) => {
                  const roleBadge =
                    u.role === 'superadmin' || u.role === 'tenant_admin'
                      ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30'
                      : u.role === 'org_admin'
                        ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/30'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';

                  const roleLabel =
                    u.role === 'superadmin' || u.role === 'tenant_admin'
                      ? 'Superadmin'
                      : u.role === 'org_admin'
                        ? 'Org Admin'
                        : 'Munkatárs';

                  return (
                    <button
                      key={u.email}
                      type="button"
                      onClick={() => handleQuickDemoLogin(u.email)}
                      className="text-left p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-indigo-500/50 hover:bg-indigo-50/50 dark:hover:bg-slate-800/60 transition group cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] px-1.5 py-0.2 rounded border font-semibold ${roleBadge}`}>
                          {roleLabel}
                        </span>
                        {!orgInfo && u.tenant_name && (
                          <span className="text-[10px] text-slate-400 truncate max-w-[90px]" title={u.tenant_name}>
                            {u.tenant_name}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-medium text-slate-900 dark:text-slate-200 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                        {u.name || u.email}
                      </div>
                      {u.name && (
                        <div className="text-[10px] text-slate-400 truncate">
                          {u.email}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FORGOT PASSWORD MODAL */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                  <Key className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Elfelejtett Jelszó</h3>
              </div>
              <button
                onClick={() => setShowForgotModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-400">
              Adja meg a regisztrált elsődleges vagy értesítési e-mail címét. Egy biztonságos, egyszer használatos hivatkozást küldünk a jelszó módosításához.
            </p>

            <div className="p-2.5 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 rounded-xl text-[11px] text-blue-700 dark:text-blue-300">
              Biztonsági követelmény: az értesítő üzenet kizárólag a hivatkozást tartalmazza, közvetlen kódot nem!
            </div>

            {forgotMsg && (
              <div
                className={`p-3 rounded-xl text-xs flex flex-col gap-2 ${forgotMsg.type === 'success'
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                    : 'bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300'
                  }`}
              >
                <div className="flex items-center gap-2">
                  {forgotMsg.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 shrink-0" />
                  )}
                  <span>{forgotMsg.text}</span>
                </div>

                {/* Direct link for local/testing environment */}
                {forgotMsg.link && (
                  <div className="pt-2 border-t border-emerald-200 dark:border-emerald-800/60 mt-1">
                    <a
                      href={forgotMsg.link}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-500 transition shadow-xs"
                    >
                      <span>Visszaállító hivatkozás megnyitása</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-3 pt-1">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  E-mail Cím *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="pelda@onkormanyzat.hu"
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotModal(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-medium transition"
                >
                  Bezárás
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-sm transition flex items-center gap-1.5 disabled:opacity-50"
                >
                  {forgotLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Hivatkozás Kérése</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
